import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './common/Header';
import Footer from './common/Footer';
import SEO from '../components/common/SEO';
import { formatPrice } from '../utils/priceUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const MODULE_LABELS = { holiday: 'Holiday', hotel: 'Hotel', cab: 'Cab', yatra: 'Yatra', doctor: 'Doctor', hospital: 'Hospital' };
const slugify = (value) => String(value || '').toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-').replace(/^-+|-+$/g, '');

const listingUrl = (item) => {
  if (item.type === 'city') {
    const mod = item.module || (Array.isArray(item.modules) && item.modules[0]) || 'holiday';
    if (mod === 'hotel') return `/hotel-list?city=${encodeURIComponent(item.name || '')}`;
    if (mod === 'yatra') return `/yatra-list?city=${encodeURIComponent(item.name || '')}`;
    if (mod === 'cab') return `/cabs/search?q=${encodeURIComponent(item.name || '')}`;
    if (mod === 'doctor' || mod === 'hospital') return `/medicaltourism?city=${encodeURIComponent(item.name || '')}`;
    return `/holidays-list?city=${encodeURIComponent(item.name || '')}`;
  }
  const price = Math.round(item.offer_price || item.price || 0);
  const slug = [slugify(item.name), slugify(item.city), price || null, item.id].filter(Boolean).join('-');
  if (item.type === 'holiday') return `/holiday-details/${slug}`;
  if (item.type === 'hotel') return `/hotel-details/${slug}`;
  if (item.type === 'yatra') return `/yatra-details/${slug}`;
  if (item.type === 'doctor' || item.type === 'hospital') return `/medical-details/${item.type}/${slug}`;
  if (item.type === 'cab') return `/cabs/search?q=${encodeURIComponent(item.name || '')}`;
  return '/';
};

const SearchResultsPage = () => {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search).get('q')?.trim() || '', [location.search]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchResults = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/search`, { params: { q: query, limit: 100 } });
        if (active) setResults(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Search API error:', error);
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (query) fetchResults(); else { setResults([]); setLoading(false); }
    return () => { active = false; };
  }, [query]);

  return (
    <>
      <SEO title={`Search${query ? `: ${query}` : ''} | Mangoloops`} description="Search Mangoloops holidays, hotels, cabs, yatras, doctors and hospitals." />
      <Header />
      <main className="bg-light py-5 min-vh-100">
        <div className="container">
          <h3 className="mb-1">Search Results</h3>
          <p className="text-muted mb-4">{query ? `Results for “${query}”` : 'Enter a search term on the home page.'}</p>
          {loading ? <div className="text-center py-5">Loading results...</div> : (
            <div className="row">
              {results.map((item) => (
                <div className="col-md-6 col-lg-4 mb-3" key={`${item.type}-${item.id}`}>
                  <Link to={listingUrl(item)} className="card h-100 shadow-sm border-0 p-3 text-decoration-none text-dark">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <h5 className="mb-2">{item.name}</h5>
                      <span className="badge bg-primary">{MODULE_LABELS[item.type] || item.type}</span>
                    </div>
                    {item.city && <p className="text-muted mb-1"><i className="fa fa-map-marker me-1" />{item.city}</p>}
                    {(item.offer_price || item.price) && (
                      <div className="d-flex align-items-baseline gap-2 mt-auto">
                        {item.offer_price && item.price && Number(item.price) > Number(item.offer_price) && (
                          <del className="text-muted small" style={{ textDecoration: 'line-through' }}>
                            ₹{formatPrice(Math.round(item.price))}
                          </del>
                        )}
                        <p className="text-success fw-bold mb-0">
                          ₹{formatPrice(Math.round(item.offer_price || item.price))}
                        </p>
                      </div>
                    )}
                  </Link>
                </div>
              ))}
              {!results.length && query && <div className="col-12 text-center py-5">No matching holidays, hotels, cabs, yatras, doctors or hospitals found.</div>}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SearchResultsPage;
